import React from 'react';
import Box from '@mui/material/Box';
import PatternAnimalSymbols, { hasAnimalSymbol, isSingleAnimalSymbol } from './PatternAnimalSymbols';
import PatternLibrarySymbol from './PatternLibrarySymbol';
import PatternToken, { QuantityToken } from './PatternToken';
import { patternChoiceSequenceLength } from './patternChoiceLayout';
import { PatternSizeRow, usePatternSize } from './PatternSizeContext';
import { PATTERN_SIZE_TOKENS } from './patternSizePolicy';
import { PositionStepPrompt, PositionLineValue, isPositionLine, isPositionLinePrompt } from './PatternPosition';
import PatternMirrorGrid from './PatternMirrorGrid';

const directButtonSx = (disabled, selected, correct, revealCorrect) => ({
  appearance: 'none',
  border: revealCorrect || (selected && correct)
    ? '3px solid #16a34a'
    : selected
    ? '3px solid #ef4444'
    : '2px solid transparent',
  bgcolor: revealCorrect || (selected && correct) ? '#dcfce7' : selected ? '#fee2e2' : 'transparent',
  borderRadius: 3,
  p: 0.35,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled && !selected && !revealCorrect ? 0.72 : 1,
  transition: 'transform 160ms ease, background-color 160ms ease',
  '&:hover': disabled ? {} : { bgcolor: '#f5f3ff', transform: 'translateY(-2px)' },
  '&:focus-visible': { outline: '4px solid rgba(124,58,237,0.35)', outlineOffset: 2 },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', transform: 'none' },
});

function PositionCard({ value }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, p: 0.7, border: '2px solid #c4b5fd', borderRadius: 2.5, bgcolor: '#fff' }}>
      {(value?.cells || []).map((cell, index) => <PatternToken key={index} token={cell} size="small" />)}
    </Box>
  );
}

function PositionStateValue({ value, decorative = false }) {
  const columns = Math.max(2, Math.min(value?.rows === 1 ? 5 : 4, Number(value?.columns || 2)));
  const rows = Math.max(1, Math.min(3, Number(value?.rows || (columns === 2 ? 2 : 1))));
  const position = Math.max(1, Math.min(columns * rows, Number(value?.position || 1)));
  const cellCount = columns * rows;
  return (
    <Box
      component="span"
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : value?.label || 'noktanın konumu'}
      sx={{
        display: 'inline-grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 0.35,
        p: 0.45,
        border: '2px solid #ddd6fe',
        borderRadius: 2,
        bgcolor: '#fff',
        flex: '0 0 auto',
      }}
    >
      {Array.from({ length: cellCount }, (_, index) => {
        const active = index + 1 === position;
        return (
          <Box
            key={index}
            component="span"
            data-testid="position-state-slot"
            data-position-slot={index + 1}
            sx={{
              width: { xs: 13, sm: 16 },
              height: { xs: 13, sm: 16 },
              display: 'grid',
              placeItems: 'center',
              border: '1px solid #c4b5fd',
              borderRadius: 0.75,
              bgcolor: active ? '#ede9fe' : '#fff',
            }}
          >
            {active ? <Box component="span" data-testid="position-active-dot" sx={{ width: '58%', height: '58%', borderRadius: '50%', bgcolor: '#312e81' }} /> : null}
          </Box>
        );
      })}
    </Box>
  );
}

function PositionPathValue({ value }) {
  const positions = value?.positions || [];
  return (
    <Box
      component="span"
      role="img"
      aria-label={value?.label || 'konum yolu'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        gap: { xs: 0.25, sm: 0.45 },
        maxWidth: '100%',
      }}
    >
      {positions.map((position, index) => (
        <React.Fragment key={`${position}-${index}`}>
          {index > 0 ? <Box component="span" aria-hidden="true" sx={{ color: '#7c3aed', fontSize: { xs: 14, sm: 18 }, fontWeight: 1000 }}>→</Box> : null}
          <PositionStateValue
            value={{
              columns: Number(value?.columns || 2),
              rows: Number(value?.rows || 2),
              position: Number.isFinite(Number(position))
                ? Number(position)
                : ["top_left", "top_right", "bottom_left", "bottom_right"].indexOf(position) + 1,
              label: position,
            }}
            decorative
          />
        </React.Fragment>
      ))}
    </Box>
  );
}

function PositionFixValue({ value }) {
  return (
    <Box
      component="span"
      role="img"
      aria-label={value?.label || 'konum düzeltmesi'}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.55 }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          minWidth: 24,
          height: 24,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '50%',
          bgcolor: '#ede9fe',
          color: '#5b21b6',
          fontSize: 14,
          fontWeight: 1000,
        }}
      >
        {value?.step}.
      </Box>
      <PositionStateValue value={value?.state} decorative />
    </Box>
  );
}

function FillSymbolValue({ value, variant }) {
  const compact = variant === 'compact_prompt';
  return (
    <Box
      component="span"
      role="img"
      aria-label={value?.label || 'doluluk'}
      sx={{
        width: compact ? { xs: 24, sm: 29 } : { xs: 34, sm: 40 },
        height: compact ? { xs: 24, sm: 29 } : { xs: 34, sm: 40 },
        display: 'grid',
        placeItems: 'center',
        border: '2px solid #ddd6fe',
        borderRadius: '50%',
        bgcolor: '#fff',
      }}
    >
      <Box component="span" aria-hidden="true" sx={{ width: '54%', height: '54%', border: '2px solid #312e81', borderRadius: '50%', bgcolor: value?.filled ? '#312e81' : '#fff' }} />
    </Box>
  );
}

function TwoBinsValue({ value, variant }) {
  const compact = variant === 'compact_prompt' || variant === 'choice';
  const counts = [Math.max(0, Math.min(10, Number(value?.left || 0))), Math.max(0, Math.min(10, Number(value?.right || 0)))];
  return (
    <Box component="span" role="img" aria-label={value?.label || 'iki kutu'} sx={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 0.3 : 0.55 }}>
      {counts.map((count, index) => (
        <React.Fragment key={index}>
          {index === 1 ? <Box component="span" aria-hidden="true" sx={{ color: '#a78bfa', fontWeight: 1000 }}>|</Box> : null}
          <Box component="span" sx={{ width: compact ? 37 : 48, minHeight: compact ? 28 : 36, display: 'flex', flexWrap: 'wrap', alignContent: 'center', justifyContent: 'center', gap: 0.2, p: 0.35, border: '2px solid #c4b5fd', borderRadius: 1.5, bgcolor: '#fff' }}>
            {Array.from({ length: count }, (_, dotIndex) => <Box key={dotIndex} component="span" sx={{ width: compact ? 5 : 6, height: compact ? 5 : 6, borderRadius: '50%', bgcolor: '#312e81' }} />)}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}

function MirrorAxis({ axis, compact = false }) {
  if (axis === 'vertical') {
    return <Box aria-hidden="true" sx={{ width: 0, minHeight: compact ? 38 : 58, borderLeft: '3px dashed #a78bfa' }} />;
  }
  return <Box aria-hidden="true" sx={{ width: compact ? 52 : 68, borderTop: '3px dashed #a78bfa' }} />;
}

function MirrorGlyph({ glyph, unknown = false, compact = false }) {
  const multiSymbol = String(glyph || '').length > 1;
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: compact ? { xs: multiSymbol ? 44 : 30, sm: multiSymbol ? 52 : 34 } : { xs: multiSymbol ? 58 : 40, sm: multiSymbol ? 66 : 48 },
        height: compact ? { xs: 30, sm: 34 } : { xs: 40, sm: 48 },
        display: 'grid',
        placeItems: 'center',
        border: unknown ? '3px dashed #a78bfa' : '2px solid #ddd6fe',
        borderRadius: '50%',
        bgcolor: '#fff',
        color: '#312e81',
        fontFamily: '"Segoe UI Symbol", "Apple Symbols", sans-serif',
        fontSize: compact ? { xs: multiSymbol ? 20 : 22, sm: multiSymbol ? 24 : 26 } : { xs: multiSymbol ? 23 : 27, sm: multiSymbol ? 27 : 32 },
        fontWeight: 900,
      }}
    >
      {unknown ? '?' : isDirectionGlyph(glyph) ? <DirectionGlyph direction={glyph} size={compact ? 22 : 28} /> : glyph}
    </Box>
  );
}

function MirrorSceneValue({ value, variant }) {
  const axis = value?.axis || 'vertical';
  const target = value?.reflected;
  const compact = variant === 'choice';
  if (axis === 'none') {
    return (
      <Box component="span" role="img" aria-label="ayna dönüşümü" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <MirrorGlyph glyph={value?.source} compact={compact} />
        <Box component="span" aria-hidden="true" sx={{ color: '#a78bfa', fontSize: 26, fontWeight: 900 }}>?</Box>
        <MirrorGlyph glyph={target} compact={compact} />
      </Box>
    );
  }
  const vertical = axis === 'vertical';
  return (
    <Box
      component="span"
      role="img"
      aria-label="ayna görüntüsü"
      sx={{
        display: 'inline-flex',
        flexDirection: vertical ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
      }}
    >
      <MirrorGlyph glyph={value?.source} compact={compact} />
      <MirrorAxis axis={axis} compact={compact} />
      <MirrorGlyph glyph={target} unknown={!target} compact={compact} />
    </Box>
  );
}

function MirrorPairsValue({ value }) {
  const pairs = value?.pairs || [];
  return (
    <Box component="span" role="img" aria-label="ayna çiftleri" sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.45 }}>
      {pairs.map((pair, index) => (
        <Box key={index} component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.55 }}>
          <Box component="span" sx={{ width: 17, color: '#6d28d9', fontSize: 13, fontWeight: 1000 }}>{index + 1}</Box>
          {[pair?.[0], pair?.[1]].map((glyph, glyphIndex) => (
            <React.Fragment key={glyphIndex}>
              {glyphIndex === 1 ? <Box aria-hidden="true" sx={{ height: 32, borderLeft: '2px dashed #a78bfa' }} /> : null}
              <Box component="span" aria-hidden="true" sx={{ width: 30, height: 30, display: 'grid', placeItems: 'center', border: '1px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontFamily: '"Segoe UI Symbol", "Apple Symbols", sans-serif', fontSize: 21, fontWeight: 900 }}>{glyph}</Box>
            </React.Fragment>
          ))}
        </Box>
      ))}
    </Box>
  );
}

function AxisSymbolValue({ value }) {
  const axis = value?.axis || 'none';
  return (
    <Box component="span" role="img" aria-label={value?.label || 'ayna ekseni'} sx={{ display: 'grid', placeItems: 'center', width: 70, height: 54 }}>
      {axis === 'none' ? (
        <Box component="span" aria-hidden="true" sx={{ color: '#312e81', fontSize: 31, fontWeight: 900 }}>∅</Box>
      ) : (
        <Box aria-hidden="true" sx={axis === 'vertical'
          ? { height: 38, borderLeft: '4px dashed #6d28d9' }
          : { width: 52, borderTop: '4px dashed #6d28d9' }}
        />
      )}
    </Box>
  );
}

function MirrorRuleValue() {
  return (
    <Box component="span" role="img" aria-label="ayna kuralı" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
      <DirectionGlyph direction="→" size={28} />
      <Box aria-hidden="true" sx={{ height: 42, borderLeft: '3px dashed #a78bfa' }} />
      <DirectionGlyph direction="←" size={28} />
    </Box>
  );
}

const directionRotations = { '↑': 0, '↗': 45, '→': 90, '↘': 135, '↓': 180, '↙': 225, '←': 270, '↖': 315 };
const isDirectionGlyph = (value) => Object.prototype.hasOwnProperty.call(directionRotations, value);

function DirectionGlyph({ direction, size }) {
  return (
    <Box component="span" aria-hidden="true" sx={{ display: 'inline-grid', placeItems: 'center', width: size, height: size, color: '#312e81', transform: `rotate(${directionRotations[direction] || 0}deg)` }}>
      <Box component="svg" viewBox="0 0 64 64" sx={{ width: '100%', height: '100%', overflow: 'visible' }}>
        <path d="M32 54V13M17 28L32 13L47 28" fill="none" stroke="currentColor" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
      </Box>
    </Box>
  );
}

function ChoiceSequence({ items, label, sharedLength, role }) {
  const count = Math.max(1, items.length, sharedLength || 0);
  const columns = Math.ceil(count / Math.ceil(count / 8));
  const rows = Array.from({ length: Math.ceil(items.length / columns) }, (_, index) => (
    items.slice(index * columns, (index + 1) * columns)
  ));
  const cellWidth = `calc((100% - ${(columns - 1) * 4}px) / ${columns})`;
  const fontSize = (cap) => `min(${cap}px, calc((100cqw - ${(columns - 1) * 4}px) / ${columns} * 0.68))`;
  return (
    <Box
      component="span"
      role={role}
      aria-label={label}
      data-testid="pattern-choice-sequence"
      data-columns={columns}
      data-rows={rows.length}
      sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%', minWidth: 0, containerType: 'inline-size', rowGap: '6px' }}
    >
      {rows.map((row, rowIndex) => (
        <Box key={rowIndex} component="span" sx={{ display: 'flex', width: '100%', minWidth: 0, justifyContent: 'center', gap: '4px' }}>
          {row.map((item, index) => (
            <Box
              key={index}
              component="span"
              aria-hidden={role === 'img' || undefined}
              sx={{
                width: cellWidth, maxWidth: { xs: 48, sm: 56, md: 64 }, aspectRatio: '1',
                flex: '0 0 auto', minWidth: 0, boxSizing: 'border-box',
                display: 'grid', placeItems: 'center', border: '2px solid #ddd6fe',
                borderRadius: '50%', bgcolor: '#fff', color: '#312e81',
                fontSize: { xs: fontSize(32), sm: fontSize(38), md: fontSize(43) },
                fontWeight: 900, lineHeight: 1,
              }}
            >
              {item}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

function SequenceTokensValue({ value, variant, choiceSequenceLength }) {
  const compact = variant === 'choice';
  const tokens = value?.tokens || [];
  if (compact) {
    return (
      <ChoiceSequence
        label={value?.label || tokens.join(' ')}
        role="img"
        sharedLength={choiceSequenceLength}
        items={tokens.map((token) => (
          isDirectionGlyph(token)
            ? <DirectionGlyph direction={token} size="1em" />
            : <Box component="span" sx={{ fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", sans-serif', fontSize: `${Math.min(1, 2 / Array.from(String(token)).length)}em` }}>{token}</Box>
        ))}
      />
    );
  }
  const directionSequence = tokens.length > 1 && tokens.every((token) => ['↑', '↓', '←', '→', '↗', '↘', '↙', '↖'].includes(token));
  if (directionSequence) {
    return (
      <Box component="span" role="img" aria-label={value?.label || 'yön dizisi'} sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: compact ? 0.35 : 0.55, px: compact ? 0.25 : 0.45 }}>
        <Box aria-hidden="true" sx={{ position: 'absolute', left: compact ? 16 : 20, right: compact ? 16 : 20, top: '50%', borderTop: '2px dashed #c4b5fd', zIndex: 0 }} />
        {tokens.map((token, index) => (
          <Box key={`${token}-${index}`} component="span" aria-hidden="true" sx={{ position: 'relative', zIndex: 1, width: compact ? 31 : 38, height: compact ? 31 : 38, display: 'grid', placeItems: 'center', border: '2px solid #c4b5fd', borderRadius: '50%', bgcolor: '#fff', boxShadow: '0 2px 4px rgba(91,33,182,0.12)' }}><DirectionGlyph direction={token} size={compact ? 23 : 28} /></Box>
        ))}
      </Box>
    );
  }
  return (
    <Box component="span" role="img" aria-label={value?.label || 'dizi'} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: compact ? 0.35 : 0.55 }}>
      {tokens.map((token, index) => (
        <Box key={`${token}-${index}`} component="span" aria-hidden="true" sx={{ width: compact ? 27 : 34, height: compact ? 27 : 34, display: 'grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontSize: compact ? 17 : 20, fontWeight: 900 }}>{isDirectionGlyph(token) ? <DirectionGlyph direction={token} size={compact ? 17 : 21} /> : token}</Box>
      ))}
    </Box>
  );
}

function PairSwapValue({ value }) {
  const tokens = value?.tokens || [];
  const pairs = value?.pairs || [];
  return (
    <Box
      component="span"
      role="img"
      aria-label={value?.label || 'yer değiştirme kuralı'}
      sx={{ display: 'inline-grid', justifyItems: 'center', gap: { xs: 0.65, sm: 0.9 }, maxWidth: '100%' }}
    >
      <Box component="span" aria-hidden="true" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.7, sm: 1.1 } }}>
        {pairs.map(([leftIndex, rightIndex], index) => (
          <Box key={`${leftIndex}-${rightIndex}-${index}`} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.4 } }}>
            <Box component="span" sx={{ width: { xs: 31, sm: 36 }, height: { xs: 31, sm: 36 }, display: 'grid', placeItems: 'center', border: '2px solid #c4b5fd', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontSize: { xs: 19, sm: 22 }, fontWeight: 900 }}>{tokens[leftIndex]}</Box>
            <Box component="span" sx={{ color: '#7c3aed', fontSize: { xs: 24, sm: 28 }, fontWeight: 1000, lineHeight: 1 }}>{'\u21C4'}</Box>
            <Box component="span" sx={{ width: { xs: 31, sm: 36 }, height: { xs: 31, sm: 36 }, display: 'grid', placeItems: 'center', border: '2px solid #c4b5fd', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontSize: { xs: 19, sm: 22 }, fontWeight: 900 }}>{tokens[rightIndex]}</Box>
          </Box>
        ))}
      </Box>
      <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.3, sm: 0.45 } }}>
        {tokens.map((token, index) => <Box key={`${token}-${index}`} component="span" sx={{ width: { xs: 28, sm: 33 }, height: { xs: 28, sm: 33 }, display: 'grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontSize: { xs: 17, sm: 20 }, fontWeight: 900 }}>{token}</Box>)}
        <Box component="span" sx={{ color: '#7c3aed', fontSize: { xs: 22, sm: 27 }, fontWeight: 1000, mx: 0.1 }}>{'\u2192'}</Box>
        <Box component="span" sx={{ width: { xs: 34, sm: 40 }, height: { xs: 34, sm: 40 }, display: 'grid', placeItems: 'center', border: '3px dashed #a78bfa', borderRadius: '50%', bgcolor: '#fff', color: '#7c3aed', fontSize: { xs: 22, sm: 26 }, fontWeight: 1000 }}>?</Box>
      </Box>
    </Box>
  );
}

function StateProgressionValue({ value, variant }) {
  const compact = variant === 'choice';
  const states = value?.states || [];
  const unit = compact ? 24 : 25;
  return (
    <Box component="span" role="img" aria-label={value?.label || 'durum dizisi'} sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: compact ? 0.5 : 0.7, maxWidth: '100%', minWidth: 0 }}>
      {states.map((state, index) => (
        <Box key={index} component="span" data-testid="pattern-state-step" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, maxWidth: '100%', minWidth: 0 }}>
          {index ? <Box component="span" aria-hidden="true" sx={{ color: '#7c3aed', fontWeight: 1000, fontSize: compact ? 16 : 20 }}>→</Box> : null}
          <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 0.25, minWidth: state.length ? 0 : unit + 8, minHeight: unit + 8, px: compact ? 0.35 : 0.5, py: 0.3, border: state.length ? '2px solid #ddd6fe' : '2px dashed #a78bfa', borderRadius: 2, bgcolor: '#fff', color: '#7c3aed' }}>
            {state.length
              ? state.map((token, tokenIndex) => <Box key={`${token}-${tokenIndex}`} component="span" aria-hidden="true" sx={{ width: unit, height: unit, flexShrink: 0, display: 'grid', placeItems: 'center', color: '#312e81', fontSize: 18, fontWeight: 900 }}>{token}</Box>)
              : <Box component="span" aria-hidden="true" sx={{ fontSize: compact ? 18 : 22, fontWeight: 1000 }}>?</Box>}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function TurnValue({ value }) {
  const angle = Number(value?.angle || 0);
  const direction = value?.direction;
  const glyph = direction === 'clockwise' ? '↻' : direction === 'counterclockwise' ? '↺' : '↻';
  return <Box component="span" role="img" aria-label={value?.label || 'dönüş'} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.45, color: '#312e81', fontSize: 23, fontWeight: 1000 }}><Box component="span" aria-hidden="true" sx={{ color: '#7c3aed', fontSize: 30, lineHeight: 1 }}>{glyph}</Box><Box component="span">{angle}°</Box></Box>;
}

const contourGlyphs = { square: '⊏', triangle: '⌃', corner: '└', arc: '◜', house: '⌂', arrow: '▷', vertical: '│', plus: '✚', circle: '◯', bridge: '∩', double: '⌒⌒', closed: '□', horizontal: '─', diagonal: '╱', empty: '·' };

function ContourValue({ value, scene = false }) {
  const glyph = contourGlyphs[value?.scene || value?.icon] || '└';
  return <Box component="span" role="img" aria-label={value?.label || 'eksik şekil'} sx={{ width: scene ? 78 : 54, height: scene ? 62 : 48, display: 'grid', placeItems: 'center', border: scene ? '3px dashed #a78bfa' : '2px solid #ddd6fe', borderRadius: 2.5, bgcolor: '#fff', color: '#312e81', fontFamily: '"Segoe UI Symbol", sans-serif', fontSize: scene ? 43 : 34, fontWeight: 900 }}>{glyph}</Box>;
}

function NumberSequenceValue({ value, variant }) {
  const compact = variant === 'choice';
  const values = value?.values || [];
  const roomy = values.length <= 2;
  const tokenSize = compact ? (roomy ? 40 : 31) : (roomy ? 44 : 36);
  const fontSize = compact ? (roomy ? 26 : 19) : (roomy ? 28 : 21);
  return <Box component="span" role="img" aria-label={value?.label || 'sayı dizisi'} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: compact ? 0.25 : 0.45 }}>{values.map((number, index) => <React.Fragment key={`${number}-${index}`}>{index ? <Box component="span" aria-hidden="true" sx={{ color: '#a78bfa', fontWeight: 900 }}>→</Box> : null}<Box component="span" aria-hidden="true" sx={{ minWidth: tokenSize, height: tokenSize, display: 'grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontSize, fontWeight: 900 }}>{number}</Box></React.Fragment>)}</Box>;
}

function MatrixTokenValue({ value, variant }) {
  const questionSize = usePatternSize();
  if (questionSize.enabled) return <IntrinsicSizeValue value={value} sizeToken={value?.size || 'medium'} variant={variant} />;
  const scale = value?.size === 'large' ? 1.35 : value?.size === 'small' ? 0.72 : 1;
  return <Box component="span" role="img" aria-label={value?.label || 'tablo simgesi'} sx={{ display: 'grid', placeItems: 'center', color: value?.color || '#312e81', fontFamily: '"Segoe UI Symbol", sans-serif', fontSize: `${(variant === 'choice' ? 29 : 26) * scale}px`, lineHeight: 1, fontWeight: 900 }}>{value?.symbol}</Box>;
}

function RotationSceneValue({ value }) {
  return <Box component="span" role="img" aria-label="dönüş" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7 }}><MirrorGlyph glyph={value?.source} /><Box component="span" aria-hidden="true" sx={{ color: '#7c3aed', fontWeight: 1000, fontSize: 19 }}>↻{value?.angle ?? '?' }°</Box><MirrorGlyph unknown /></Box>;
}

function RotationAngleValue({ value }) {
  return <Box component="span" role="img" aria-label={value?.label || 'dönüş açısı'} sx={{ display: 'inline-flex', alignItems: 'center', color: '#312e81', fontSize: 24, fontWeight: 1000 }}>↻{value?.angle}°</Box>;
}

function RotationGridValue({ value }) {
  return <Box component="span" role="img" aria-label="nokta dönüşü" sx={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, 18px)', gap: 0.4, p: 0.6, border: '2px solid #ddd6fe', borderRadius: 2, bgcolor: '#fff' }}>{Array.from({ length: 9 }, (_, index) => <Box key={index} sx={{ width: 18, height: 18, display: 'grid', placeItems: 'center', border: '1px solid #c4b5fd', borderRadius: 0.6 }}>{(value?.positions || []).includes(index + 1) ? <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#312e81' }} /> : null}</Box>)}<Box component="span" sx={{ gridColumn: '1 / -1', color: '#7c3aed', fontWeight: 1000, fontSize: 15, textAlign: 'center' }}>↻{value?.angle ?? '?'}°</Box></Box>;
}

function MultiPositionValue({ value }) {
  return <RotationGridValue value={{ positions: value?.positions || [] }} />;
}

function RotationOrMirrorValue({ value }) {
  return <Box component="span" role="img" aria-label={value?.label || 'dönüş'} sx={{ color: '#312e81', fontSize: 30, fontWeight: 1000 }}>{value?.mode === 'mirror' ? '↔' : value?.mode === 'rotate90' ? '↻90°' : '↻270°'}</Box>;
}

function MiniMatrixValue({ value }) {
  const questionSize = usePatternSize();
  const cellSize = questionSize.enabled ? questionSize.slot : 22;
  return <Box component="span" role="img" aria-label={value?.label || 'mini tablo'} data-pattern-size-grid-columns={value?.columns || 2} sx={{ display: 'inline-grid', gridTemplateColumns: `repeat(${Number(value?.columns || 2)}, ${cellSize}px)`, gap: 0.25, p: 0.35, border: '2px solid #ddd6fe', borderRadius: 1.5, bgcolor: '#fff' }}>{(value?.cells || []).map((cell, index) => <Box key={index} sx={{ width: cellSize, height: cellSize, display: 'grid', placeItems: 'center', border: '1px solid #c4b5fd', borderRadius: 0.5 }}><MatrixTokenValue value={cell} variant="choice" /></Box>)}</Box>;
}

function MatrixRuleValue() {
  return <Box component="span" role="img" aria-label="satır sütun kuralı" sx={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, 15px)', gap: 0.25, p: 0.55, border: '2px solid #a78bfa', borderRadius: 2, bgcolor: '#fff' }}>{Array.from({ length: 9 }, (_, index) => <Box key={index} sx={{ width: 15, height: 15, border: '1px solid #c4b5fd', borderRadius: 0.4, bgcolor: index === 4 ? '#ede9fe' : '#fff' }} />)}</Box>;
}

function NumberReplacementValue({ value }) {
  return <Box component="span" role="img" aria-label={value?.label || 'sayı düzeltmesi'} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}><Box component="span" sx={{ minWidth: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: '50%', bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 900 }}>{value?.from}</Box><Box component="span" aria-hidden="true" sx={{ color: '#7c3aed', fontWeight: 1000 }}>→</Box><Box component="span" sx={{ minWidth: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: '50%', bgcolor: '#dcfce7', color: '#166534', fontWeight: 900 }}>{value?.to}</Box></Box>;
}

function PairedNumberSequencesValue({ value, variant }) {
  const compact = variant === 'choice';
  return (
    <Box component="span" role="img" aria-label={value?.label || 'iki sayı dizisi'} sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: compact ? 0.2 : 0.45 }}>
      {(value?.series || []).map((numbers, index) => (
        <Box key={index} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
          <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: index ? '#22c55e' : '#7c3aed' }} />
          <NumberSequenceValue value={{ values: numbers }} variant={compact ? 'choice' : 'compact_prompt'} />
        </Box>
      ))}
    </Box>
  );
}

function CoordinateSequencesValue({ value, variant }) {
  const compact = variant === 'choice';
  return (
    <Box component="span" role="img" aria-label={value?.label || 'iki koordinat dizisi'} sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: compact ? 0.2 : 0.4 }}>
      {(value?.series || []).map((series, lineIndex) => (
        <Box key={lineIndex} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 0.2 : 0.4 }}>
          <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: lineIndex ? '#22c55e' : '#7c3aed' }} />
          {series.map((pair, index) => <React.Fragment key={`${pair.join('-')}-${index}`}><Box component="span" aria-hidden="true" sx={{ minWidth: compact ? 31 : 38, height: compact ? 25 : 30, display: 'grid', placeItems: 'center', border: '1.5px solid #c4b5fd', borderRadius: 1.5, bgcolor: '#fff', color: '#312e81', fontSize: compact ? 12 : 14, fontWeight: 900 }}>{pair[0]}·{pair[1]}</Box>{index < series.length - 1 ? <Box component="span" aria-hidden="true" sx={{ color: '#a78bfa', fontWeight: 900 }}>→</Box> : null}</React.Fragment>)}
        </Box>
      ))}
    </Box>
  );
}

function RouteValue({ value, variant }) {
  const compact = variant === 'choice';
  return <NumberSequenceValue value={{ values: value?.steps || [], label: value?.label || 'izlenen yol' }} variant={compact ? 'choice' : 'compact_prompt'} />;
}

function LabeledGridValue({ value }) {
  const columns = Math.max(2, Math.min(3, Number(value?.columns || 3)));
  const rows = Math.max(2, Math.min(3, Number(value?.rows || 3)));
  const marks = value?.marks || [];
  return (
    <Box component="span" role="img" aria-label={value?.label || 'işaretli tablo'} sx={{ display: 'inline-grid', gridTemplateColumns: `repeat(${columns}, 20px)`, gap: 0.35, p: 0.55, border: '2px solid #ddd6fe', borderRadius: 2, bgcolor: '#fff' }}>
      {Array.from({ length: columns * rows }, (_, index) => {
        const mark = marks.find((item) => Number(item.position) === index + 1);
        return <Box key={index} component="span" sx={{ width: 20, height: 20, display: 'grid', placeItems: 'center', border: '1px solid #c4b5fd', borderRadius: 0.65, bgcolor: mark ? '#ede9fe' : '#fff', color: '#312e81', fontSize: 12, fontWeight: 1000 }}>{mark?.token || ''}</Box>;
      })}
    </Box>
  );
}

function MappingValue({ value }) {
  return <Box component="span" role="img" aria-label={value?.label || 'eşleme kuralı'} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.55 }}>{(value?.pairs || []).map((pair, index) => <React.Fragment key={`${pair?.[0]}-${pair?.[1]}-${index}`}><Box component="span" aria-hidden="true" sx={{ minWidth: 24, height: 24, display: 'grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontWeight: 900 }}>{pair?.[0]}</Box><Box component="span" aria-hidden="true" sx={{ color: '#7c3aed', fontWeight: 1000 }}>→</Box><Box component="span" aria-hidden="true" sx={{ minWidth: 24, height: 24, display: 'grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontWeight: 900 }}>{pair?.[1]}</Box>{index < (value?.pairs || []).length - 1 ? <Box component="span" aria-hidden="true" sx={{ width: 1, height: 24, bgcolor: '#ddd6fe', mx: 0.15 }} /> : null}</React.Fragment>)}</Box>;
}

function ConditionRuleValue({ value }) {
  const actionGlyphs = { rotate: '↻', grow: '◉', fill: '●', empty: '○', keep: '✓' };
  return <Box component="span" role="img" aria-label={value?.label || 'koşullu kural'} sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.35 }}>{(value?.rules || []).map((rule, index) => <Box key={index} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}><Box component="span" aria-hidden="true" sx={{ width: 25, height: 25, display: 'grid', placeItems: 'center', border: '2px solid #c4b5fd', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontWeight: 1000 }}>{rule?.condition === 'filled' ? '●' : rule?.condition === 'empty' ? '○' : rule?.condition === 'red' ? '●' : '◆'}</Box><Box component="span" aria-hidden="true" sx={{ color: '#7c3aed', fontWeight: 1000 }}>→</Box><Box component="span" aria-hidden="true" sx={{ minWidth: 25, height: 25, display: 'grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: 1.5, bgcolor: '#fff', color: rule?.condition === 'red' ? '#ef4444' : '#312e81', fontSize: 19, fontWeight: 1000 }}>{actionGlyphs[rule?.action] || '↻'}</Box></Box>)}</Box>;
}

function FeatureValue({ value }) {
  const feature = value?.feature || 'shape';
  const glyph = feature === 'color' ? '●' : feature === 'size' ? '◉' : feature === 'fill' ? '◐' : feature === 'position' ? '⌖' : feature === 'object' ? '🐱' : feature === 'all' ? '✦' : feature === 'none' ? '∅' : '▲';
  const color = feature === 'color' ? '#ef4444' : '#312e81';
  return <Box component="span" role="img" aria-label={value?.label || 'özellik'} sx={{ width: 46, height: 46, display: 'grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color, fontSize: 28, fontWeight: 1000 }}>{glyph}</Box>;
}

function MatrixAxesValue({ value }) {
  const mode = value?.mode || 'row_color_col_size';
  const rows = mode === 'row_shape_col_fill' ? ['●', '▲', '■'] : mode === 'latin' ? ['A', 'B', 'C'] : ['●', '●', '●'];
  const columns = mode === 'row_shape_col_fill' ? ['●', '○', '●'] : mode === 'latin' ? ['A', 'B', 'C'] : ['•', '●', '◉'];
  const colors = ['#ef4444', '#3b82f6', '#22c55e'];
  return <Box component="span" role="img" aria-label={value?.label || 'satır ve sütun tablosu'} sx={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, 19px)', gap: 0.3, p: 0.5, border: '2px solid #ddd6fe', borderRadius: 2, bgcolor: '#fff' }}>{Array.from({ length: 9 }, (_, index) => { const row = Math.floor(index / 3); const column = index % 3; const invalid = mode === 'same' || (mode === 'abab' && row !== 1); return <Box key={index} component="span" sx={{ width: 19, height: 19, display: 'grid', placeItems: 'center', border: '1px solid #c4b5fd', borderRadius: 0.55, color: mode === 'row_color_col_size' ? colors[row] : '#312e81', fontSize: mode === 'row_color_col_size' ? [10, 14, 18][column] : 12, fontWeight: 1000 }}>{invalid ? (mode === 'same' ? 'A' : row % 2 ? 'B' : 'A') : mode === 'latin' ? columns[(row + column) % 3] : mode === 'row_shape_col_fill' ? `${rows[row]}${columns[column]}` : ['▲', '■', '●'][column]}</Box>; })}</Box>;
}

function SetOperationValue({ value }) {
  const mode = value?.mode || 'intersection';
  return <Box component="span" role="img" aria-label={value?.label || 'küme işlemi'} sx={{ position: 'relative', width: 74, height: 46, display: 'inline-block' }}><Box component="span" aria-hidden="true" sx={{ position: 'absolute', left: 5, top: 5, width: 36, height: 36, border: '2px solid #7c3aed', borderRadius: '50%', bgcolor: 'rgba(196,181,253,0.3)' }} /><Box component="span" aria-hidden="true" sx={{ position: 'absolute', right: 5, top: 5, width: 36, height: 36, border: '2px solid #22c55e', borderRadius: '50%', bgcolor: 'rgba(187,247,208,0.3)' }} /><Box component="span" aria-hidden="true" sx={{ position: 'absolute', left: mode === 'intersection' ? 30 : mode === 'left' ? 15 : 46, top: 18, width: 12, height: 12, borderRadius: '50%', bgcolor: '#312e81' }} /></Box>;
}

function ClosureSvg({ scene, piece }) {
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 7, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const dotted = { ...stroke, stroke: '#a78bfa', strokeWidth: 3, strokeDasharray: '5 5' };
  if (piece) {
    const paths = {
      right_edge: <path {...stroke} d="M72 18V82" />,
      bottom_edge: <path {...stroke} d="M25 62H115" />,
      corner_br: <path {...stroke} d="M42 20V70H105" />,
      corner_tl: <path {...stroke} d="M108 82V32H45" />,
      peak: <path {...stroke} d="M32 76L70 20L108 76" />,
      arc_cw: <path {...stroke} d="M35 70 Q72 16 110 55" />,
      arc_ccw: <path {...stroke} d="M35 35 Q72 89 110 50" />,
      arc_match: <><circle cx="42" cy="20" r="4" fill="#a78bfa" /><circle cx="106" cy="62" r="4" fill="#a78bfa" /><path {...stroke} d="M42 20A42 42 0 0 1 106 62" /></>,
      arc_line: <><circle cx="42" cy="20" r="4" fill="#a78bfa" /><circle cx="106" cy="62" r="4" fill="#a78bfa" /><path {...stroke} d="M42 20L106 62" /></>,
      arc_reverse: <><circle cx="42" cy="20" r="4" fill="#a78bfa" /><circle cx="106" cy="62" r="4" fill="#a78bfa" /><path {...stroke} d="M42 20Q96 14 106 62" /></>,
      roof_right: <path {...stroke} d="M34 22L108 75" />,
      arrow_lower: <path {...stroke} d="M30 78L92 48" />,
      diagonal: <path {...stroke} d="M30 78L108 22" />,
      vertical: <path {...stroke} d="M70 18V82" />,
      horizontal: <path {...stroke} d="M25 50H115" />,
      bridge: <path {...stroke} d="M18 50H122" />,
      stub: <path {...stroke} d="M30 50H68" />,
      zigzag: <path {...stroke} d="M28 70L55 30L82 70L110 30" />,
      ring: <circle {...stroke} cx="70" cy="50" r="27" />,
      corner_cell: <path {...stroke} d="M43 72V35H95" />,
      empty: null,
      // Kesik çizgi, sorudaki eksik alanı anlatır. Şıklardaki parçalar ise
      // gerçek adaylardır; düz çizilince çocuk bunları “boşluk işareti” gibi
      // değil, yerleştirilecek parça olarak daha kolay ayırt eder.
      open_corner: <path {...stroke} d="M34 28H75V69" />,
      open_single: <path {...stroke} d="M35 50H82" />,
      open_parallel: <path {...stroke} d="M30 34H110M30 66H110" />,
      reflect: <><path {...stroke} d="M35 35L70 50L35 65" /><path {...dotted} d="M80 18V82" /><path {...stroke} d="M105 35L70 50L105 65" /></>,
      rotate: <><path {...stroke} d="M42 65L70 25L98 65Z" /><path {...dotted} d="M70 12A38 38 0 0 1 108 50" /></>,
      fill_center: <rect x="53" y="33" width="34" height="34" rx="3" fill="currentColor" />,
    };
    return <>{paths[piece] || paths.empty}</>;
  }
  const scenes = {
    square_right_gap: <><path {...stroke} d="M28 18H108M28 18V82H108" /><path {...dotted} d="M108 18V82" /></>,
    triangle_base_gap: <><path {...stroke} d="M30 76L70 18L110 76" /><path {...dotted} d="M30 76H110" /></>,
    corner_br_gap: <><path {...stroke} d="M24 18H110V53M24 18V82H74" /><path {...dotted} d="M110 53V82H74" /></>,
    circle_quarter_gap: <><path {...stroke} d="M70 16A36 36 0 1 0 116 58" /><path {...dotted} d="M116 58A36 36 0 0 0 70 16" /><circle cx="70" cy="16" r="4" fill="#a78bfa" /><circle cx="116" cy="58" r="4" fill="#a78bfa" /></>,
    house_roof_gap: <><path {...stroke} d="M28 82V49L68 18M28 82H108V49" /><path {...dotted} d="M68 18L108 49" /></>,
    arrow_lower_gap: <><path {...stroke} d="M22 50H70L106 24" /><path {...dotted} d="M106 76L70 50" /></>,
    plus_center_gap: <><rect {...stroke} x="42" y="16" width="24" height="24" /><rect {...stroke} x="74" y="16" width="24" height="24" /><rect {...stroke} x="16" y="44" width="24" height="24" /><rect {...dotted} x="48" y="44" width="24" height="24" /><rect {...stroke} x="80" y="44" width="24" height="24" /><rect {...stroke} x="42" y="72" width="24" height="24" /><rect {...stroke} x="74" y="72" width="24" height="24" /></>,
    bridge_gap: <><path {...stroke} d="M18 50H51M89 50H122" /><path {...dotted} d="M51 50H89" /></>,
    hidden_corner_gap: <><path {...stroke} d="M18 50H50M92 82V50" /><rect {...dotted} x="50" y="30" width="42" height="52" rx="6" /></>,
  };
  return scenes[scene] || scenes.corner_br_gap;
}

function ClosureSceneValue({ value, variant }) {
  const compact = variant === 'choice';
  return <Box component="span" role="img" aria-label={value?.label || 'eksik şekil'} sx={{ width: compact ? 98 : 154, height: compact ? 68 : 102, display: 'grid', placeItems: 'center', color: '#312e81', flex: '0 0 auto' }}><Box component="svg" viewBox="0 0 140 100" aria-hidden="true" sx={{ width: '100%', height: '100%' }}><ClosureSvg scene={value?.scene} /></Box></Box>;
}

function ClosurePieceValue({ value, variant }) {
  const compact = variant === 'choice';
  return <Box component="span" role="img" aria-label={value?.label || 'parça'} sx={{ width: compact ? 92 : 120, height: compact ? 62 : 80, display: 'grid', placeItems: 'center', color: '#312e81', flex: '0 0 auto' }}><Box component="svg" viewBox="0 0 140 100" aria-hidden="true" sx={{ width: '100%', height: '100%' }}><ClosureSvg piece={value?.piece} /></Box></Box>;
}

const sizeSymbolFontSizes = {
  xsmall: {
    prompt: { xs: 12, sm: 15, md: 17 },
    choice: { xs: 18, sm: 21, md: 24 },
  },
  small: {
    prompt: { xs: 18, sm: 22, md: 25 },
    choice: { xs: 25, sm: 29, md: 33 },
  },
  medium: {
    prompt: { xs: 26, sm: 31, md: 35 },
    choice: { xs: 34, sm: 40, md: 45 },
  },
  large: {
    prompt: { xs: 34, sm: 40, md: 45 },
    choice: { xs: 45, sm: 52, md: 58 },
  },
  xlarge: {
    prompt: { xs: 39, sm: 47, md: 53 },
    choice: { xs: 52, sm: 60, md: 66 },
  },
};

const semanticSizeLabels = { xsmall: 'çok küçük', small: 'küçük', medium: 'orta', large: 'büyük', xlarge: 'çok büyük' };
const semanticShapeNames = { '●': 'daire', '○': 'daire', '▲': 'üçgen', '△': 'üçgen', '■': 'kare', '□': 'kare', '◆': 'baklava', '◇': 'baklava' };

function IntrinsicSizeValue({ value, sizeToken = 'medium', variant }) {
  const questionSize = usePatternSize();
  const pixels = PATTERN_SIZE_TOKENS[sizeToken] * questionSize.scale;
  const glyph = value?.symbol || value?.glyph || '●';
  const library = value?.kind === 'library_symbol';
  const sizeLabel = semanticSizeLabels[sizeToken];
  const label = library && value?.label?.startsWith(`${sizeLabel} `)
    ? value.label : `${sizeLabel} ${library ? value?.label || 'simge' : semanticShapeNames[glyph] || value?.label || glyph}`;
  const outline = ['○', '△', '□', '◇'].includes(glyph);
  const svgProps = { fill: outline ? 'none' : 'currentColor', stroke: outline ? 'currentColor' : 'none', strokeWidth: 6 };
  let shape;
  if (['●', '○'].includes(glyph)) shape = <circle cx="50" cy="50" r={outline ? 47 : 50} {...svgProps} />;
  if (['■', '□'].includes(glyph)) shape = <rect x={outline ? 3 : 0} y={outline ? 3 : 0} width={outline ? 94 : 100} height={outline ? 94 : 100} {...svgProps} />;
  if (['▲', '△'].includes(glyph)) shape = <polygon points={outline ? '50,3 97,97 3,97' : '50,0 100,100 0,100'} strokeLinejoin="round" {...svgProps} />;
  if (['◆', '◇'].includes(glyph)) shape = <polygon points={outline ? '50,3 97,50 50,97 3,50' : '50,0 100,50 50,100 0,50'} strokeLinejoin="round" {...svgProps} />;
  // Emoji are HTML text, including symbol-library entries with shape fallbacks.
  // An HTML span inside an SVG namespace is present in the DOM but not painted.
  if (library) shape = null;
  return (
    <Box component="span" role="img" aria-label={label} data-pattern-size-item="true" data-size-token={sizeToken} sx={{ width: `${questionSize.slot}px`, height: `${questionSize.slot}px`, flex: '0 0 auto', display: 'inline-grid', placeItems: 'center', boxSizing: 'border-box', borderRadius: '50%', border: variant === 'choice' ? '1px solid #ddd6fe' : '1px solid transparent', bgcolor: variant === 'choice' ? '#fff' : 'transparent', color: value?.color || '#312e81' }}>
      <Box component={shape ? 'svg' : 'span'} viewBox={shape ? '0 0 100 100' : undefined} aria-hidden="true" data-testid="pattern-size-glyph" data-pattern-size-glyph="true" data-size-token={sizeToken} sx={{ width: `${pixels}px`, height: `${pixels}px`, display: 'inline-grid', placeItems: 'center', fontSize: `${pixels}px`, fontFamily: '"Segoe UI Symbol", "Apple Symbols", sans-serif', fontWeight: 1000, lineHeight: 1, flexShrink: 0 }}>
        {library ? <PatternLibrarySymbol value={value} decorative /> : shape || (isDirectionGlyph(glyph) ? <DirectionGlyph direction={glyph} size="1em" /> : glyph)}
      </Box>
    </Box>
  );
}

function SizeSymbolValue({ value, variant }) {
  const questionSize = usePatternSize();
  if (questionSize.enabled) return <IntrinsicSizeValue value={value} sizeToken={value?.visual_size || 'medium'} variant={variant} />;
  const choice = variant === 'choice';
  const compactPrompt = variant === 'compact_prompt';
  const visualSize = sizeSymbolFontSizes[value?.visual_size] ? value.visual_size : 'medium';
  const baseFontSize = sizeSymbolFontSizes[visualSize][choice ? 'choice' : 'prompt'];
  return (
    <Box
      component="span"
      role="img"
      aria-label={value?.label || visualSize}
      sx={{
        width: choice
          ? { xs: 66, sm: 76, md: 86 }
          : compactPrompt
          ? { xs: 36, sm: 54, md: 62 }
          : { xs: 42, sm: 54, md: 62 },
        height: choice
          ? { xs: 66, sm: 76, md: 86 }
          : compactPrompt
          ? { xs: 36, sm: 54, md: 62 }
          : { xs: 42, sm: 54, md: 62 },
        display: 'inline-grid',
        placeItems: 'center',
        flex: '0 0 auto',
        borderRadius: '50%',
        bgcolor: choice ? '#fff' : 'transparent',
        border: choice ? '2px solid #ddd6fe' : '2px solid transparent',
        color: '#312e81',
        fontFamily: '"Segoe UI Symbol", "Apple Symbols", sans-serif',
        fontSize: compactPrompt
          ? { ...baseFontSize, xs: Math.min(Number(baseFontSize.xs), 28) }
          : baseFontSize,
        fontWeight: 1000,
        lineHeight: 1,
      }}
    >
      {value?.symbol || '●'}
    </Box>
  );
}

function SizeSequenceValue({ value, variant }) {
  const questionSize = usePatternSize();
  if (questionSize.enabled) return <PatternSizeRow connector="→" items={(value?.sizes || []).map((size) => <SizeSymbolValue value={{ visual_size: size, symbol: value?.symbol || '●' }} variant={variant} />)} />;
  const compact = variant === 'choice';
  const sizes = value?.sizes || [];
  return (
    <Box component="span" role="img" aria-label={value?.label || 'boyut dizisi'} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: compact ? 0.1 : 0.3, maxWidth: '100%' }}>
      {sizes.map((size, index) => (
        <React.Fragment key={`${size}-${index}`}>
          {index ? <Box component="span" aria-hidden="true" sx={{ color: '#a78bfa', fontWeight: 1000, fontSize: compact ? 13 : 17 }}>→</Box> : null}
          <SizeSymbolValue value={{ visual_size: size, symbol: value?.symbol || '●', label: size }} variant="compact_prompt" />
        </React.Fragment>
      ))}
    </Box>
  );
}

const attributeGlyphs = {
  circle: { filled: '●', empty: '○' },
  triangle: { filled: '▲', empty: '△' },
  square: { filled: '■', empty: '□' },
};

function AttributeSequenceValue({ value, variant }) {
  const questionSize = usePatternSize();
  const compact = variant === 'choice';
  const items = value?.items || [];
  if (questionSize.enabled) {
    return <PatternSizeRow connector={value?.connector || ''} items={items.map((item) => <IntrinsicSizeValue value={{ glyph: item?.glyph || attributeGlyphs[item?.shape || 'circle']?.[item?.filled === false ? 'empty' : 'filled'] || '●', color: item?.color === 'red' ? '#ef4444' : item?.color === 'blue' ? '#2563eb' : '#312e81' }} sizeToken={item?.size || 'medium'} variant={variant} />)} />;
  }
  const fontSizes = compact
    ? { small: 17, medium: 21, large: 26, xlarge: 30 }
    : { small: 21, medium: 26, large: 32, xlarge: 38 };
  return (
    <Box component="span" role="img" aria-label={value?.label || 'özellik dizisi'} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: compact ? 0.2 : 0.35, maxWidth: '100%' }}>
      {items.map((item, index) => {
        const glyph = item?.glyph || attributeGlyphs[item?.shape || 'circle']?.[item?.filled === false ? 'empty' : 'filled'] || '●';
        const size = fontSizes[item?.size] || fontSizes.medium;
        const color = item?.color === 'red' ? '#ef4444' : item?.color === 'blue' ? '#2563eb' : '#312e81';
        return (
          <React.Fragment key={`${glyph}-${index}`}>
            {index && value?.connector ? <Box component="span" aria-hidden="true" sx={{ color: '#a78bfa', fontWeight: 1000, fontSize: compact ? 13 : 17 }}>{value.connector}</Box> : null}
            <Box component="span" aria-hidden="true" sx={{ width: compact ? 25 : 32, height: compact ? 28 : 34, display: 'grid', placeItems: 'center', color, fontFamily: '"Segoe UI Symbol", "Apple Symbols", sans-serif', fontSize: size, fontWeight: 1000, lineHeight: 1 }}>{isDirectionGlyph(glyph) ? <DirectionGlyph direction={glyph} size={Math.max(16, Math.min(size, compact ? 20 : 26))} /> : glyph}</Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

function DualRhythmValue({ value, variant }) {
  const questionSize = usePatternSize();
  const compact = variant === 'choice';
  return (
    <Box component="span" role="img" aria-label={value?.label || 'iki ritim'} sx={{ display: 'inline-flex', width: questionSize.enabled ? '100%' : undefined, minWidth: 0, flexDirection: 'column', alignItems: 'center', gap: compact ? 0.05 : 0.2 }}>
      <AttributeSequenceValue value={{ items: (value?.directions || []).map((glyph) => ({ glyph })), connector: '→', label: 'yön ritmi' }} variant={variant} />
      <AttributeSequenceValue value={{ items: (value?.sizes || []).map((size) => ({ shape: 'circle', size })), connector: '→', label: 'boyut ritmi' }} variant={variant} />
    </Box>
  );
}

function StreamPairValue({ value, variant }) {
  const compact = variant === 'choice';
  const streams = value?.streams || [];
  return (
    <Box component="span" role="img" aria-label={value?.label || 'iki kaynak akışı'} sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: compact ? 0.05 : 0.2, maxWidth: '100%' }}>
      {streams.map((stream, streamIndex) => (
        <Box key={streamIndex} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 0.2 : 0.35 }}>
          <Box component="span" aria-hidden="true" sx={{ width: compact ? 6 : 8, height: compact ? 6 : 8, borderRadius: '50%', bgcolor: streamIndex ? '#22c55e' : '#7c3aed' }} />
          {(stream || []).map((token, index) => <React.Fragment key={`${token}-${index}`}><Box component="span" aria-hidden="true" sx={{ minWidth: compact ? 20 : 25, height: compact ? 20 : 25, display: 'grid', placeItems: 'center', border: '1.5px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontSize: compact ? 11 : 13, fontWeight: 900 }}>{isDirectionGlyph(token) ? <DirectionGlyph direction={token} size={compact ? 15 : 18} /> : token}</Box>{index < stream.length - 1 ? <Box component="span" aria-hidden="true" sx={{ color: '#a78bfa', fontSize: compact ? 11 : 14, fontWeight: 1000 }}>→</Box> : null}</React.Fragment>)}
        </Box>
      ))}
    </Box>
  );
}

function SymbolFixValue({ value, variant }) {
  const compact = variant === 'choice';
  return (
    <Box component="span" role="img" aria-label={value?.label || 'simgesi düzeltilen öğe'} sx={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 0.45 : 0.65 }}>
      <Box component="span" aria-hidden="true" sx={{ width: compact ? 40 : 42, height: compact ? 40 : 42, display: 'grid', placeItems: 'center', borderRadius: '50%', bgcolor: '#ede9fe', color: '#5b21b6', fontSize: compact ? 28 : 30, fontWeight: 1000, lineHeight: 1 }}>{value?.step}.</Box>
      <Box component="span" aria-hidden="true" sx={{ width: compact ? 40 : 42, height: compact ? 40 : 42, display: 'grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontSize: compact ? 18 : 23, fontWeight: 1000 }}>{isDirectionGlyph(value?.symbol) ? <DirectionGlyph direction={value.symbol} size={30} /> : value?.symbol || '●'}</Box>
    </Box>
  );
}

function BinProgressionValue({ value, variant }) {
  const compact = variant === 'choice';
  const states = value?.states || [];
  return (
    <Box component="span" role="img" aria-label={value?.label || 'kutu aktarımı'} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: compact ? 0.15 : 0.35, maxWidth: '100%' }}>
      {states.map((state, index) => (
        <React.Fragment key={`${state?.left}-${state?.right}-${index}`}>
          {index ? <Box component="span" aria-hidden="true" sx={{ color: '#a78bfa', fontWeight: 1000, fontSize: compact ? 13 : 17 }}>→</Box> : null}
          <TwoBinsValue value={{ left: state?.left, right: state?.right, label: `${state?.left} ve ${state?.right}` }} variant={compact ? 'choice' : 'compact_prompt'} />
        </React.Fragment>
      ))}
    </Box>
  );
}

function RotationMatchValue({ value, variant }) {
  const compact = variant === 'choice';
  const matches = Boolean(value?.matches);
  return (
    <Box component="span" role="img" aria-label={value?.label || (matches ? 'dönünce eşleşir' : 'eşleşmez')} sx={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 0.35 : 0.5, color: matches ? '#166534' : '#b91c1c', fontWeight: 1000 }}>
      <Box component="span" aria-hidden="true" sx={{ width: compact ? 24 : 30, height: compact ? 24 : 30, display: 'grid', placeItems: 'center', borderRadius: '50%', bgcolor: matches ? '#dcfce7' : '#fee2e2', fontSize: compact ? 16 : 19 }}>{matches ? '✓' : '×'}</Box>
      {matches ? <Box component="span" aria-hidden="true" sx={{ color: '#312e81', fontSize: compact ? 17 : 22 }}>↻{value?.angle}°</Box> : null}
    </Box>
  );
}

function regularPolygonPoints(sides) {
  const count = Math.max(3, Number(sides || 3));
  const startAngle = count === 4 ? -Math.PI / 4 : -Math.PI / 2;
  return Array.from({ length: count }, (_, index) => {
    const angle = startAngle + ((Math.PI * 2 * index) / count);
    return `${32 + (24 * Math.cos(angle))},${32 + (24 * Math.sin(angle))}`;
  }).join(' ');
}

function PolygonSymbolValue({ value, variant }) {
  const choice = variant === 'choice';
  const compactPrompt = variant === 'compact_prompt';
  const size = choice
    ? { xs: 66, sm: 76, md: 86 }
    : compactPrompt
    ? { xs: 40, sm: 54, md: 62 }
    : { xs: 50, sm: 58, md: 66 };
  const circle = value?.shape_id === 'circle' || Number(value?.sides || 0) < 3;
  return (
    <Box
      component="span"
      role="img"
      aria-label={value?.label || 'çokgen'}
      sx={{
        width: size,
        height: size,
        display: 'inline-grid',
        placeItems: 'center',
        flex: '0 0 auto',
        borderRadius: '50%',
        bgcolor: '#fff',
        border: '2px solid #ddd6fe',
        color: '#312e81',
      }}
    >
      <Box component="svg" viewBox="0 0 64 64" aria-hidden="true" sx={{ width: '76%', height: '76%' }}>
        {circle ? (
          <circle cx="32" cy="32" r="23" fill="#ede9fe" stroke="currentColor" strokeWidth="4" />
        ) : (
          <polygon
            points={regularPolygonPoints(value?.sides)}
            fill="#ede9fe"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinejoin="round"
          />
        )}
      </Box>
    </Box>
  );
}

function PolygonSequenceValue({ value, variant }) {
  const compact = variant === 'choice';
  const polygons = value?.polygons || [];
  return (
    <Box component="span" role="img" aria-label={value?.label || 'çokgen dizisi'} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: compact ? 0.1 : 0.3, maxWidth: '100%' }}>
      {polygons.map((polygon, index) => (
        <React.Fragment key={`${polygon.sides}-${index}`}>
          {index ? <Box component="span" aria-hidden="true" sx={{ color: '#a78bfa', fontWeight: 1000, fontSize: compact ? 13 : 17 }}>→</Box> : null}
          <PolygonSymbolValue value={polygon} variant="compact_prompt" />
        </React.Fragment>
      ))}
    </Box>
  );
}

function IntensitySymbolValue({ value, variant }) {
  const choice = variant === 'choice';
  const compactPrompt = variant === 'compact_prompt';
  const level = Math.min(5, Math.max(1, Number(value?.intensity_level || 3)));
  const opacities = [0.12, 0.3, 0.52, 0.76, 1];
  const size = choice
    ? { xs: 66, sm: 76, md: 86 }
    : compactPrompt
    ? { xs: 40, sm: 54, md: 62 }
    : { xs: 50, sm: 58, md: 66 };
  return (
    <Box
      component="span"
      role="img"
      aria-label={value?.label || 'renk tonu'}
      sx={{
        width: size,
        height: size,
        display: 'inline-grid',
        placeItems: 'center',
        flex: '0 0 auto',
        borderRadius: '50%',
        bgcolor: '#fff',
        border: '2px solid #ddd6fe',
      }}
    >
      <Box
        component="span"
        sx={{
          width: '62%',
          height: '62%',
          borderRadius: '50%',
          bgcolor: `rgba(49, 46, 129, ${opacities[level - 1]})`,
          border: level === 1 ? '2px solid rgba(49, 46, 129, 0.24)' : '2px solid transparent',
        }}
      />
    </Box>
  );
}

function LibrarySymbolValue({ value, variant }) {
  const questionSize = usePatternSize();
  if (questionSize.enabled && value?.visual_size) return <IntrinsicSizeValue value={value} sizeToken={value.visual_size} variant={variant} />;
  const choice = variant === 'choice';
  const compactPrompt = variant === 'compact_prompt';
  const sizedFontSizes = {
    xsmall: choice ? { xs: 22, sm: 26, md: 30 } : { xs: 17, sm: 21, md: 24 },
    small: choice ? { xs: 31, sm: 36, md: 41 } : { xs: 24, sm: 29, md: 33 },
    medium: choice ? { xs: 40, sm: 46, md: 52 } : { xs: 31, sm: 36, md: 41 },
    large: choice ? { xs: 49, sm: 57, md: 64 } : { xs: 38, sm: 44, md: 50 },
    xlarge: choice ? { xs: 55, sm: 64, md: 72 } : { xs: 43, sm: 50, md: 56 },
  };
  const visualSize = sizedFontSizes[value?.visual_size] ? value.visual_size : null;
  return (
    <Box
      component="span"
      sx={{
        width: choice
          ? { xs: 66, sm: 76, md: 86 }
          : compactPrompt
          ? { xs: 36, sm: 58, md: 66 }
          : { xs: 50, sm: 58, md: 66 },
        height: choice
          ? { xs: 66, sm: 76, md: 86 }
          : compactPrompt
          ? { xs: 36, sm: 58, md: 66 }
          : { xs: 50, sm: 58, md: 66 },
        display: 'inline-grid',
        placeItems: 'center',
        flex: '0 0 auto',
        borderRadius: '50%',
        bgcolor: '#fff',
        border: '2px solid #ddd6fe',
        color: '#312e81',
        fontSize: visualSize
          ? sizedFontSizes[visualSize]
          : choice
          ? { xs: 46, sm: 54, md: 61 }
          : compactPrompt
          ? { xs: 27, sm: 42, md: 48 }
          : { xs: 36, sm: 42, md: 48 },
        lineHeight: 1,
      }}
    >
      <PatternLibrarySymbol value={value} />
    </Box>
  );
}

function LibrarySequenceValue({ value, variant, choiceSequenceLength }) {
  const parts = value?.parts || [];
  const symbolCount = parts.filter((part) => part?.kind === 'library_symbol').length;
  const choice = variant === 'choice';
  if (choice && patternChoiceSequenceLength(value)) {
    return (
      <ChoiceSequence
        label={value?.label}
        sharedLength={choiceSequenceLength}
        items={parts.filter((part) => part?.kind === 'library_symbol')
          .map((part) => <PatternLibrarySymbol value={part} />)}
      />
    );
  }
  const symbolSize = choice && symbolCount > 4
    ? { xs: 29, sm: 34, md: 38 }
    : choice
    ? { xs: 36, sm: 42, md: 47 }
    : { xs: 34, sm: 40, md: 45 };
  return (
    <Box
      component="span"
      aria-label={value?.label}
      sx={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 0.45, sm: 0.65 },
        maxWidth: '100%',
        color: '#312e81',
        fontSize: { xs: 17, sm: 20, md: 22 },
        fontWeight: 850,
        lineHeight: 1.25,
      }}
    >
      {parts.map((part, index) => (
        part?.kind === 'library_symbol' ? (
          <Box
            key={`${part.symbol_id}-${index}`}
            component="span"
            sx={{
              width: symbolSize,
              height: symbolSize,
              display: 'inline-grid',
              placeItems: 'center',
              borderRadius: '50%',
              bgcolor: '#fff',
              border: '1.5px solid #ddd6fe',
              fontSize: symbolSize,
              lineHeight: 1,
            }}
          >
            <PatternLibrarySymbol value={part} />
          </Box>
        ) : (
          <Box key={`text-${index}`} component="span" sx={{ whiteSpace: 'pre-wrap' }}>
            {part?.text}
          </Box>
        )
      ))}
    </Box>
  );
}

function LibraryQuantityValue({ value }) {
  const count = Math.max(0, Number(value?.count || 0));
  if (count === 0) {
    return (
      <Box
        component="span"
        role="img"
        aria-label={value?.label || 'hiç yok'}
        sx={{ width: 38, height: 38, display: 'inline-grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontSize: 20, fontWeight: 900 }}
      >
        0
      </Box>
    );
  }
  return (
    <Box
      component="span"
      aria-label={value?.label}
      sx={{ display: 'inline-flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5, maxWidth: 300 }}
    >
      {Array.from({ length: count }, (_, index) => (
        <Box
          key={index}
          component="span"
          sx={{ width: { xs: 34, sm: 40 }, height: { xs: 34, sm: 40 }, display: 'inline-grid', placeItems: 'center', color: '#312e81', fontSize: { xs: 33, sm: 39 } }}
        >
          <PatternLibrarySymbol value={value?.symbol} decorative />
        </Box>
      ))}
    </Box>
  );
}

function DotQuantityValue({ value, variant }) {
  const count = Math.max(0, Number(value?.count || 0));
  const compact = variant === 'choice';
  if (count === 0) {
    return <Box component="span" role="img" aria-label={value?.label || 'hiç nokta yok'} sx={{ width: compact ? 34 : 42, height: compact ? 34 : 42, display: 'inline-grid', placeItems: 'center', border: '2px solid #ddd6fe', borderRadius: '50%', bgcolor: '#fff', color: '#312e81', fontSize: compact ? 18 : 22, fontWeight: 900 }}>0</Box>;
  }
  return (
    <Box component="span" role="img" aria-label={value?.label || `${count} nokta`} sx={{ display: 'inline-grid', gridTemplateColumns: `repeat(${Math.min(3, count)}, ${compact ? 14 : 16}px)`, gap: compact ? 0.35 : 0.45, justifyContent: 'center', alignItems: 'center', minWidth: compact ? 48 : 58, minHeight: compact ? 32 : 40, px: 0.55, py: 0.4, border: '2px solid #ddd6fe', borderRadius: 2, bgcolor: '#fff' }}>
      {Array.from({ length: count }, (_, index) => <Box key={index} component="span" aria-hidden="true" sx={{ width: compact ? 14 : 16, height: compact ? 14 : 16, borderRadius: '50%', bgcolor: '#312e81' }} />)}
    </Box>
  );
}

export function PatternValue({ value, variant = 'prompt', choiceSequenceLength }) {
  const questionSize = usePatternSize();
  const compactPrompt = variant === 'compact_prompt';
  if (!questionSize.enabled && ((isPositionLine(value) && variant === 'choice') || variant === 'position_reference')) {
    return <PositionLineValue value={value} variant={variant} />;
  }
  if (value?.kind === 'quantity') return <QuantityToken value={value} />;
  if (value?.kind === 'dot_quantity') return <DotQuantityValue value={value} variant={variant} />;
  if (value?.kind === 'position_state') return variant === 'mirror_choice'
    ? <PatternMirrorGrid value={value} /> : <PositionStateValue value={value} />;
  if (value?.kind === 'position_path') return <PositionPathValue value={value} />;
  if (value?.kind === 'position_fix') return <PositionFixValue value={value} />;
  if (value?.kind === 'fill_symbol') return <FillSymbolValue value={value} variant={variant} />;
  if (value?.kind === 'two_bins') return <TwoBinsValue value={value} variant={variant} />;
  if (value?.kind === 'mirror_scene') return <MirrorSceneValue value={value} variant={variant} />;
  if (value?.kind === 'mirror_grid') return <PatternMirrorGrid value={value} reference />;
  if (value?.kind === 'mirror_pairs') return <MirrorPairsValue value={value} />;
  if (value?.kind === 'axis_symbol') return <AxisSymbolValue value={value} />;
  if (value?.kind === 'mirror_rule') return <MirrorRuleValue />;
  if (value?.kind === 'sequence_tokens') return <SequenceTokensValue value={value} variant={variant} choiceSequenceLength={choiceSequenceLength} />;
  if (value?.kind === 'pair_swap') return <PairSwapValue value={value} />;
  if (value?.kind === 'state_progression') return <StateProgressionValue value={value} variant={variant} />;
  if (value?.kind === 'turn') return <TurnValue value={value} />;
  if (value?.kind === 'contour_scene') return <ContourValue value={value} scene />;
  if (value?.kind === 'contour_piece') return <ContourValue value={value} />;
  if (value?.kind === 'number_sequence') return <NumberSequenceValue value={value} variant={variant} />;
  if (value?.kind === 'matrix_token') return <MatrixTokenValue value={value} variant={variant} />;
  if (value?.kind === 'rotation_scene') return <RotationSceneValue value={value} />;
  if (value?.kind === 'rotation_angle') return <RotationAngleValue value={value} />;
  if (value?.kind === 'rotation_grid') return <RotationGridValue value={value} />;
  if (value?.kind === 'multi_position_state') return <MultiPositionValue value={value} />;
  if (value?.kind === 'rotation_or_mirror') return <RotationOrMirrorValue value={value} />;
  if (value?.kind === 'mini_matrix') return <MiniMatrixValue value={value} />;
  if (value?.kind === 'matrix_rule') return <MatrixRuleValue />;
  if (value?.kind === 'number_replacement') return <NumberReplacementValue value={value} />;
  if (value?.kind === 'paired_number_sequences') return <PairedNumberSequencesValue value={value} variant={variant} />;
  if (value?.kind === 'coordinate_sequences') return <CoordinateSequencesValue value={value} variant={variant} />;
  if (value?.kind === 'route') return <RouteValue value={value} variant={variant} />;
  if (value?.kind === 'labeled_grid') return <LabeledGridValue value={value} />;
  if (value?.kind === 'mapping') return <MappingValue value={value} />;
  if (value?.kind === 'condition_rule') return <ConditionRuleValue value={value} />;
  if (value?.kind === 'feature') return <FeatureValue value={value} />;
  if (value?.kind === 'matrix_axes') return <MatrixAxesValue value={value} />;
  if (value?.kind === 'set_operation') return <SetOperationValue value={value} />;
  if (value?.kind === 'closure_scene') return <ClosureSceneValue value={value} variant={variant} />;
  if (value?.kind === 'closure_piece') return <ClosurePieceValue value={value} variant={variant} />;
  if (value?.layout === 'position_card') return <PositionCard value={value} />;
  if (value?.kind === 'size_symbol') return <SizeSymbolValue value={value} variant={variant} />;
  if (value?.kind === 'polygon_symbol') return <PolygonSymbolValue value={value} variant={variant} />;
  if (value?.kind === 'intensity_symbol') return <IntensitySymbolValue value={value} variant={variant} />;
  if (value?.kind === 'library_symbol') return <LibrarySymbolValue value={value} variant={variant} />;
  if (value?.kind === 'library_sequence') return <LibrarySequenceValue value={value} variant={variant} choiceSequenceLength={choiceSequenceLength} />;
  if (value?.kind === 'library_quantity') return <LibraryQuantityValue value={value} />;
  if (value?.kind === 'size_sequence') return <SizeSequenceValue value={value} variant={variant} />;
  if (value?.kind === 'attribute_sequence') return <AttributeSequenceValue value={value} variant={variant} />;
  if (value?.kind === 'dual_rhythm') return <DualRhythmValue value={value} variant={variant} />;
  if (value?.kind === 'stream_pair') return <StreamPairValue value={value} variant={variant} />;
  if (value?.kind === 'symbol_fix') return <SymbolFixValue value={value} variant={variant} />;
  if (value?.kind === 'bin_progression') return <BinProgressionValue value={value} variant={variant} />;
  if (value?.kind === 'rotation_match') return <RotationMatchValue value={value} variant={variant} />;
  if (value?.kind === 'polygon_sequence') return <PolygonSequenceValue value={value} variant={variant} />;
  if (value?.kind === 'visual') {
    const visualText = value.text || value.label || '';
    if (questionSize.enabled && (semanticShapeNames[visualText] || isDirectionGlyph(visualText))) return <IntrinsicSizeValue value={{ symbol: visualText }} variant={variant} />;
    const hasAnimal = hasAnimalSymbol(visualText);
    const singleAnimal = isSingleAnimalSymbol(visualText);
    const numericVisual = /^\d+$/.test(String(visualText).trim());
    const largeChoice = variant === 'choice' && singleAnimal;
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-grid',
          placeItems: 'center',
          minWidth: largeChoice
            ? { xs: 66, sm: 76, md: 86 }
            : compactPrompt
            ? { xs: 36, sm: 62, md: 70 }
            : { xs: 50, sm: 62, md: 70 },
          minHeight: largeChoice
            ? { xs: 66, sm: 76, md: 86 }
            : compactPrompt
            ? { xs: 36, sm: 62, md: 70 }
            : { xs: 50, sm: 62, md: 70 },
          px: compactPrompt ? { xs: 0.2, sm: singleAnimal ? 0.65 : 1 } : singleAnimal ? 0.65 : 1,
          py: compactPrompt ? { xs: 0.2, sm: singleAnimal ? 0.65 : 0.75 } : singleAnimal ? 0.65 : 0.75,
          borderRadius: singleAnimal ? '50%' : 2.5,
          bgcolor: '#fff',
          border: '2px solid #ddd6fe',
          color: '#312e81',
          whiteSpace: 'pre-wrap',
          fontFamily: '"Segoe UI Symbol", "Apple Symbols", sans-serif',
          fontSize: largeChoice
            ? { xs: 44, sm: 52, md: 58 }
            : compactPrompt
            ? (hasAnimal ? { xs: 26, sm: 42, md: 48 } : { xs: 25, sm: 33, md: 37 })
            : hasAnimal
            ? (singleAnimal ? { xs: 34, sm: 42, md: 48 } : { xs: 24, sm: 28, md: 32 })
            : numericVisual
            ? { xs: 30, sm: 33, md: 37 }
            : { xs: 27, sm: 33, md: 37 },
          lineHeight: 1.15,
          fontWeight: 900,
          textAlign: 'center',
        }}
      >
        {hasAnimal ? <PatternAnimalSymbols text={visualText} /> : isDirectionGlyph(visualText) ? <DirectionGlyph direction={visualText} size={compactPrompt ? 25 : 30} /> : visualText}
      </Box>
    );
  }
  if (value?.kind === 'text') {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          maxWidth: 320,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          fontFamily: 'inherit',
          fontSize: { xs: 17, sm: 20, md: 22 },
          lineHeight: 1.25,
          fontWeight: 850,
          textAlign: 'center',
        }}
      >
        {value.text || value.label}
      </Box>
    );
  }
  return <PatternToken token={value} size={compactPrompt ? 'compact' : 'normal'} />;
}

function InteractiveValue({ value, index, direct, selectedAttempt, disabled, revealAnswer, correctTargetIndex, onSelect, variant }) {
  const selected = direct && Number(selectedAttempt?.target_index) === index;
  const correct = selected && Boolean(selectedAttempt?.correct);
  const revealCorrect = direct && revealAnswer && Number(correctTargetIndex) === index;
  if (!direct || value?.kind === 'empty') return <PatternValue value={value} variant={variant} />;
  return (
    <Box
      component="button"
      type="button"
      aria-label={`${index}. parça, ${value?.label || 'desen parçası'}`}
      disabled={disabled}
      onClick={() => onSelect(index)}
      sx={{ ...directButtonSx(disabled, selected, correct, revealCorrect), ...(variant === 'position_reference' ? { width: '100%', minHeight: 44 } : {}) }}
    >
      <PatternValue value={value} variant={variant} />
    </Box>
  );
}

export default function PatternPrompt({
  prompt,
  direct = false,
  attempts = [],
  locked = false,
  revealAnswer = false,
  correctTargetIndex = null,
  onSelect = () => {},
}) {
  const questionSize = usePatternSize();
  const selectedMap = new Map(attempts.map((attempt) => [Number(attempt.target_index), attempt]));
  if (!questionSize.enabled && isPositionLinePrompt(prompt)) {
    return <PositionStepPrompt prompt={prompt} renderItem={(item, itemIndex) => (
      <InteractiveValue value={item} index={itemIndex + 1} variant="position_reference" direct={direct}
        selectedAttempt={selectedMap.get(itemIndex + 1)} disabled={locked || selectedMap.has(itemIndex + 1)}
        revealAnswer={revealAnswer} correctTargetIndex={correctTargetIndex} onSelect={onSelect}
      />
    )} />;
  }
  if (prompt?.layout === 'before_after') {
    const rows = [
      { key: 'before', label: 'Önce', items: prompt.before || [] },
      { key: 'after', label: 'Sonra', items: prompt.after || [] },
    ];
    return (
      <Box
        data-testid="pattern-before-after"
        sx={{
          display: 'grid',
          gap: { xs: 1, sm: 1.25 },
          minWidth: { xs: 250, sm: 320 },
          maxWidth: '100%',
        }}
      >
        {rows.map((row) => (
          <Box
            key={row.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '62px minmax(0, 1fr)', sm: '74px minmax(0, 1fr)' },
              alignItems: 'center',
              gap: { xs: 0.75, sm: 1 },
            }}
          >
            <Box
              component="span"
              sx={{
                color: '#5b21b6',
                fontSize: { xs: 17, sm: 20 },
                fontWeight: 900,
                textAlign: 'right',
              }}
            >
              {row.label}
            </Box>
            <Box
              aria-label={`${row.label}: ${row.items.map((item) => item?.label || '').join(' ')}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: { xs: 0.5, sm: 0.75 },
              }}
            >
              {row.items.map((item, index) => (
                <PatternValue key={`${row.key}-${index}`} value={item} variant="compact_prompt" />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  if (prompt?.layout === 'visual_flow') {
    if (questionSize.enabled) {
      return <PatternSizeRow connector={prompt.connector || ''} items={(prompt.items || []).map((item) => <PatternValue value={item} />)} />;
    }
    const itemCount = (prompt.items || []).length;
    const twoMobileRows = itemCount > 8;
    const compactMobile = itemCount >= 7 || (Boolean(prompt.connector) && itemCount >= 5);
    const mobileColumnCount = twoMobileRows ? Math.ceil(itemCount / 2) : Math.max(itemCount, 1);
    const compactSizeFlow = (prompt.items || []).some((item) => item?.kind === 'size_symbol');
    return (
      <Box
        data-testid="pattern-visual-flow"
        data-mobile-layout={twoMobileRows ? 'two-rows' : 'single-row'}
        data-mobile-columns={mobileColumnCount}
        sx={{
          width: compactMobile ? { xs: '100%', sm: 'auto' } : 'auto',
          maxWidth: '100%',
          display: 'grid',
          gridTemplateColumns: {
            xs: compactMobile
              ? `repeat(${mobileColumnCount}, minmax(0, 1fr))`
              : `repeat(${mobileColumnCount}, auto)`,
            sm: `repeat(${Math.max(itemCount, 1)}, auto)`,
          },
          alignItems: 'center',
          justifyContent: 'center',
          columnGap: compactSizeFlow ? { xs: 0.2, sm: 0.8, md: 1 } : { xs: 0.25, sm: 1.2 },
          rowGap: { xs: 0.75, sm: 0 },
        }}
      >
        {(prompt.items || []).map((item, index) => (
          <Box key={index} sx={{ minWidth: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.15, sm: 0.65 } }}>
            {index > 0 && prompt.connector ? (
              <Box component="span" aria-hidden="true" sx={{ color: '#7c3aed', fontWeight: 1000, fontSize: compactMobile ? { xs: 16, sm: 32 } : { xs: 24, sm: 32 } }}>
                {prompt.connector}
              </Box>
            ) : null}
            <PatternValue value={item} variant={compactMobile ? 'compact_prompt' : 'prompt'} />
          </Box>
        ))}
      </Box>
    );
  }
  if (prompt?.layout === 'attribute_tracks') {
    return (
      <Box sx={{ display: 'grid', width: questionSize.enabled ? '100%' : undefined, minWidth: 0, gap: { xs: 0.6, sm: 0.9 }, maxWidth: '100%' }}>
        {(prompt.tracks || []).map((track, trackIndex) => (
          <Box key={`${track.label}-${trackIndex}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '54px minmax(0, 1fr)', sm: '68px minmax(0, 1fr)' }, alignItems: 'center', gap: { xs: 0.45, sm: 0.75 } }}>
            <Box component="span" sx={{ color: '#5b21b6', fontSize: { xs: 13, sm: 16 }, fontWeight: 900, textAlign: 'right' }}>{track.label}</Box>
            <Box sx={{ display: 'inline-flex', minWidth: 0, alignItems: 'center', justifyContent: 'center', gap: { xs: 0.25, sm: 0.55 } }}>
              {questionSize.enabled
                ? <PatternSizeRow items={(track.items || []).map((item) => <PatternValue value={item} />)} />
                : (track.items || []).map((item, index) => <PatternValue key={index} value={item} variant="compact_prompt" />)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  if (prompt?.layout === 'visual_grid') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Number(prompt.columns || 1)}, minmax(48px, 72px))`,
          gap: { xs: 0.6, sm: 0.9 },
          p: { xs: 1, sm: 1.5 },
          borderRadius: 4,
          bgcolor: '#ede9fe',
          border: '2px solid #c4b5fd',
        }}
      >
        {(prompt.cells || []).map((cell, index) => <PatternValue key={index} value={cell} />)}
      </Box>
    );
  }
  if (prompt?.layout === 'text') {
    return (
      <Box
        sx={{
          maxWidth: 920,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          textAlign: 'center',
          color: '#312e81',
          fontSize: { xs: 21, sm: 27, md: 31 },
          lineHeight: 1.35,
          fontWeight: 900,
        }}
      >
        {prompt.text}
      </Box>
    );
  }
  if (prompt?.layout === 'rich_text') {
    return (
      <Box
        sx={{
          maxWidth: 920,
          display: 'inline-flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 0.35, sm: 0.5 },
          textAlign: 'center',
          color: '#312e81',
          fontSize: { xs: 20, sm: 26, md: 29 },
          lineHeight: 1.35,
          fontWeight: 900,
        }}
      >
        {(prompt.parts || []).map((part, index) => (
          part?.kind !== 'text' ? (
            <PatternValue key={`${part.symbol_id || part.label || 'visual'}-${index}`} value={part} variant="compact_prompt" />
          ) : (
            <Box key={`text-${index}`} component="span" sx={{ whiteSpace: 'pre-wrap' }}>
              {part?.text}
            </Box>
          )
        ))}
      </Box>
    );
  }
  if (prompt?.layout === 'matrix') {
    return (
      <Box
        data-pattern-size-grid-columns={prompt.columns || 1}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Number(prompt.columns || 1)}, minmax(52px, 84px))`,
          gap: { xs: 0.75, sm: 1.1 },
          p: { xs: 1, sm: 1.5 },
          borderRadius: 4,
          bgcolor: '#ede9fe',
          border: '2px solid #c4b5fd',
        }}
      >
        {(prompt.cells || []).map((cell, itemIndex) => (
          <Box key={itemIndex} sx={{ minWidth: 52, minHeight: 52, display: 'grid', placeItems: 'center', bgcolor: '#fff', borderRadius: 2.5, border: '1px solid #ddd6fe' }}>
            <InteractiveValue
              value={cell}
              index={itemIndex + 1}
              direct={direct}
              selectedAttempt={selectedMap.get(itemIndex + 1)}
              disabled={locked || selectedMap.has(itemIndex + 1)}
              revealAnswer={revealAnswer}
              correctTargetIndex={correctTargetIndex}
              onSelect={onSelect}
            />
          </Box>
        ))}
      </Box>
    );
  }
  if (prompt?.layout === 'position_cards') {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.8, sm: 1.5 } }}>
        {(prompt.cards || []).map((card, index) => (
          <React.Fragment key={index}>
            {index > 0 ? <Box component="span" sx={{ color: '#7c3aed', fontWeight: 900, fontSize: 24 }}>→</Box> : null}
            {card?.layout === 'position_card' ? <PositionCard value={card} /> : <PatternToken token={card} />}
          </React.Fragment>
        ))}
      </Box>
    );
  }
  if (prompt?.layout === 'quantity_sequence') {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.7, sm: 1.3 } }}>
        {(prompt.groups || []).map((group, index) => (
          <React.Fragment key={index}>
            {index > 0 ? <Box component="span" sx={{ color: '#7c3aed', fontWeight: 900, fontSize: 24 }}>→</Box> : null}
            {group?.kind === 'quantity' ? <QuantityToken value={group} /> : <PatternToken token={group} />}
          </React.Fragment>
        ))}
      </Box>
    );
  }
  if (prompt?.layout === 'symmetry') {
    const horizontal = prompt.axis === 'horizontal';
    return (
      <Box sx={{ display: 'flex', flexDirection: horizontal ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
        <PatternToken token={prompt.source} />
        <Box sx={horizontal ? { width: 96, height: 4, bgcolor: '#7c3aed', borderRadius: 99 } : { width: 4, height: 96, bgcolor: '#7c3aed', borderRadius: 99 }} />
        <PatternToken token={{ kind: 'empty', label: 'ayna karşılığı' }} />
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.45, sm: 0.8 } }}>
      {(prompt?.items || []).map((item, itemIndex) => (
        <InteractiveValue
          key={itemIndex}
          value={item}
          index={itemIndex + 1}
          direct={direct}
          selectedAttempt={selectedMap.get(itemIndex + 1)}
          disabled={locked || selectedMap.has(itemIndex + 1)}
          revealAnswer={revealAnswer}
          correctTargetIndex={correctTargetIndex}
          onSelect={onSelect}
        />
      ))}
    </Box>
  );
}
